import { agentFomoDomain } from "../../constants.ts";
import type { EventEntry } from "../../src/fields.ts";

const adminFetch = async (
  path: string,
  token: string,
  init?: RequestInit,
) => {
  const response = await fetch(`https://${agentFomoDomain}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    throw new Error(await response.text());
  }
  return response.json();
};

export const approveAdminSourceRequest = (token: string, requestId: string) =>
  adminFetch("/admin/source-requests/approve", token, {
    method: "POST",
    body: JSON.stringify({ requestId }),
  });

export const rejectAdminSourceRequest = (
  token: string,
  requestId: string,
  reason?: string,
) =>
  adminFetch("/admin/source-requests/reject", token, {
    method: "POST",
    body: JSON.stringify({ requestId, ...(reason ? { reason } : {}) }),
  });

export const fetchAdminSourceEvents = (
  token: string,
  sourceKind: "instagram-page" | "whatsapp-group" | "link-scrape-source",
  sourceRef: string,
): Promise<EventEntry[]> =>
  adminFetch(
    `/admin/source-events?sourceKind=${sourceKind}&sourceRef=${
      encodeURIComponent(sourceRef)
    }`,
    token,
  );

export type PushCountResult = {
  totalMatching: number;
  targetCount: number;
  limit: number;
  sample?: { userId: string; userName?: string; location?: string }[];
};

export type PushSendResult = {
  ok: boolean;
  sentCount: number;
  test?: boolean;
  testPhone?: string;
  error?: string;
};

export const countAdminPushRecipients = (
  token: string,
  params: {
    location?: string;
    category?: string;
    limit?: number;
  },
): Promise<PushCountResult> =>
  adminFetch("/admin/pushes/count", token, {
    method: "POST",
    body: JSON.stringify(params),
  });

export type AutoApprovalEvaluation = {
  eligible: boolean;
  reason: string;
  sourceType: "instagram" | "facebook" | "website" | "whatsapp";
  metrics?: {
    followerCount?: number;
    memberCount?: number;
    recentPostCount?: number;
    upcomingEventCount?: number;
    totalEvaluatedPosts?: number;
    eventRatio?: number;
    isScrapable?: boolean;
    isPublic?: boolean;
  };
};

export type SourcePreviewEvent = {
  name: string;
  description?: string;
  datetimeStart?: string | null;
  datetimeEnd?: string | null;
  venue?: string | null;
  city?: string | null;
  address?: string | null;
  categories?: string[];
  links?: string[];
  priceStart?: number | null;
  isUpcoming?: boolean;
};

export type SourcePreviewResult = {
  scrapable: boolean;
  status: "active" | "inactive" | "unscrapable" | "error";
  sourceType: "instagram" | "website" | "facebook" | "whatsapp";
  recentPostCount: number;
  recentEventCount: number;
  events: SourcePreviewEvent[];
  summary: string;
  reason?: string;
  followerCount?: number;
  memberCount?: number;
  autoApproval?: AutoApprovalEvaluation;
};

export type AutoApprovePendingResult = {
  totalPending: number;
  autoApprovedCount: number;
  results: Array<{
    id: string;
    title?: string;
    kind: string;
    autoApproved: boolean;
    reason: string;
  }>;
};

export const fetchAdminSourcePreview = (
  token: string,
  requestId: string,
): Promise<SourcePreviewResult> =>
  adminFetch("/admin/source-requests/preview", token, {
    method: "POST",
    body: JSON.stringify({ requestId }),
  });

export const fetchAdminAutoApprovePending = (
  token: string,
): Promise<AutoApprovePendingResult> =>
  adminFetch("/admin/source-requests/auto-approve-pending", token, {
    method: "POST",
  });

export type FollowingScanCandidate = {
  username: string;
  name: string;
  reason: string;
};

export type FollowingScanResult = {
  scannedUsername: string;
  followingCount: number;
  privateSkipped: number;
  truncated: boolean;
  candidatesFound: number;
  directAddToCoverage: boolean;
  addedToCoverage: string[];
  createdRequests: string[];
  alreadyCovered: string[];
  alreadyPending: string[];
  candidates: FollowingScanCandidate[];
};

export const scanInstagramFollowing = (
  token: string,
  params: {
    username: string;
    cityHint?: string;
    directAddToCoverage?: boolean;
  },
): Promise<FollowingScanResult> =>
  adminFetch("/admin/instagram-following/scan", token, {
    method: "POST",
    body: JSON.stringify(params),
  });

export const sendAdminPush = (
  token: string,
  params: {
    location?: string;
    category?: string;
    limit?: number;
    message: string;
    testPhone?: string;
  },
): Promise<PushSendResult> =>
  adminFetch("/admin/pushes/send", token, {
    method: "POST",
    body: JSON.stringify(params),
  });
